import { useState } from 'react';

import Button from 'components/ui/button';
import { ModalFrame } from 'components/ui/modal-frame';
import { useInitialTutorial } from 'components/user-tutorial/use-initial-tutorial';
import { actionLog } from 'services/actionlog.service';

import { withDecorativeImages } from './step-html';
import './styles.scss';

interface TutorialModalProps {
  /** Abre mesmo para quem já concluiu o tutorial. */
  open?: boolean;
  onClose?: () => void;
}

/**
 * Tutorial inicial na moldura dos modais da nova identidade (a mesma do regulamento): um passo
 * por vez, com "Anterior", "Próximo" e "Concluir" no rodapé. Fechar em qualquer passo conclui o
 * tutorial, como no carrossel antigo.
 */
export default function TutorialModal({ open, onClose }: TutorialModalProps) {
  const { steps, closeTutorial } = useInitialTutorial(open);
  const [index, setIndex] = useState(0);

  if (!steps) return null;

  const step = steps[index];
  const isFirst = index === 0;
  const isLast = index === steps.length - 1;
  const finish = () => closeTutorial(onClose);

  return (
    <ModalFrame
      isOpen
      title="Tutorial"
      subtitle={`Passo ${index + 1} de ${steps.length}`}
      onClose={finish}
      closeLabel="Fechar tutorial"
      footer={
        <>
          {steps.length > 1 && (
            <div aria-hidden="true" className="flex items-center gap-1.5">
              {steps.map((_, stepIndex) => (
                <span
                  key={stepIndex}
                  className={`h-2 rounded-full transition-[width] duration-200 ease-jj-standard ${
                    stepIndex === index ? 'w-6 bg-brand-blue' : 'w-2 bg-surface-300'
                  }`}
                />
              ))}
            </div>
          )}

          <div className="ml-auto flex gap-2.5">
            {!isFirst && (
              <Button variant="secundario" size="sm" onClick={() => setIndex(index - 1)}>
                Anterior
              </Button>
            )}
            {isLast ? (
              <Button variant="principal" size="sm" onClick={finish}>
                Concluir
              </Button>
            ) : (
              <Button variant="principal" size="sm" onClick={() => setIndex(index + 1)}>
                Próximo
              </Button>
            )}
          </div>
        </>
      }
    >
      <div key={index} className="space-y-4 px-5 py-6 lg:px-8">
        {!!step.text && <div className="tutorial-step-text" dangerouslySetInnerHTML={{ __html: withDecorativeImages(step.text) }} />}

        {!!step.video && (
          // O `tutorial_rede__c` não tem arquivo de legenda para o vídeo: sem ele, um <track>
          // vazio só enganaria a regra. Pendência de conteúdo registrada na spec.
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video
            className="aspect-video w-full rounded-jj-lg bg-black"
            src={step.video}
            preload="auto"
            autoPlay
            controls
            onEnded={event => {
              actionLog('me', 'watch_video');
              event.currentTarget.currentTime = 0;
            }}
          />
        )}
      </div>
    </ModalFrame>
  );
}
